import { ComponentType } from '@angular/cdk/overlay';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { Component, OnInit, Signal, ViewChild, computed } from '@angular/core';
import { MatMiniFabButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { cloneDeep, flatten, isEqual, uniq } from 'lodash';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  filter,
  fromEvent,
  merge,
  startWith,
  switchMap,
} from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { TReducerState } from '../../../../app.config';
import { DynamicObservable } from '../../../../core/classes/dynamic-observable';
import { EDialogWidth } from '../../../../core/constants/ui.constants';
import { ConfirmationDialogComponent } from '../../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DatatableComponent } from '../../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableConfiguration,
  TDatatableColumn,
} from '../../../../shared/components/datatable/datatable.interface';
import { IDateRangePickerDate } from '../../../../shared/components/date-range-picker/date-range-picker.interface';
import { DueDateChipComponent } from '../../../../shared/components/due-date-chip/due-date-chip.component';
import { InlineDatePickerComponent } from '../../../../shared/components/inline-date-picker/inline-date-picker.component';
import { InlineItemPriorityComponent } from '../../../../shared/components/inline-item-priority/inline-item-priority.component';
import { InlineItemStatusChipComponent } from '../../../../shared/components/inline-item-status-chip/inline-item-status-chip.component';
import { InlineUserChipComponent } from '../../../../shared/components/inline-user-chip/inline-user-chip.component';
import { ItemPriorityComponent } from '../../../../shared/components/item-priority/item-priority.component';
import { IDropdownOption } from '../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { DatatablePageConfigurationComponent } from '../../../../shared/components/page-configuration/page-configuration-content/datatable-page-configuration/datatable-page-configuration.component';
import { PageConfigurationComponent } from '../../../../shared/components/page-configuration/page-configuration.component';
import { EWidgetSortBy } from '../../../../shared/components/page-configuration/page-configuration.interface';
import { StatusCategoryBadgeComponent } from '../../../../shared/components/status-category-badge/status-category-badge.component';
import { UserChipComponent } from '../../../../shared/components/user-chip/user-chip.component';
import { userActions } from '../../../../state/user/user.actions';
import { UserEffects } from '../../../../state/user/user.effects';
import { IPageConfigurationValue, IUser } from '../../../../state/user/user.interface';
import { DateUtilitiesService } from '../../../../utilities/date-utilities.service';
import { HttpUtilitiesService } from '../../../../utilities/http-utilities.service';
import { IItemWorkInstructionAssignment } from '../../../settings/fields-field-sets/field-sets/field-sets.interface';
import { WorkInstructionEditorComponent } from '../../../settings/work-instructions/work-instruction-editor/work-instruction-editor.component';
import { ApplyChangesRecursivelyQuestionModalComponent } from '../../items-modal/apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.component';
import {
  EItemRecurrenceEditType,
  ERecursiveAction,
} from '../../items-modal/apply-changes-recursively-question-modal/apply-changes-recursively-question-modal.interface';
import { RecurringItemService } from '../../items-modal/apply-changes-recursively-question-modal/recurring-item.service';
import { ItemWorkInstructionListComponent } from '../../items-modal/item-work-instruction-list/item-work-instruction-list.component';
import { ItemsModalComponent } from '../../items-modal/items-modal.component';
import { ItemsViewControlsComponent } from '../../items-view-controls/items-view-controls.component';
import { WorkInstructionStepperComponent } from '../../work-instruction-stepper/work-instruction-stepper.component';
import { IWorkInstructionStepperParams } from '../../work-instruction-stepper/work-instruction-stepper.interface';
import { BaseItemView } from '../base-item-view.component';
import { ItemViewStore } from '../item-view.store';
import {
  IDatatableItem,
  IEmbeddedColumnsMessage,
  ILoadCustomFieldsRequest,
  defaultGeneralInformationDatatableColumns,
} from './items-datatable.interface';

@Component({
  imports: [
    MatCard,
    MatCardContent,
    DatatableComponent,
    UserChipComponent,
    AsyncPipe,
    MatIcon,
    NgIf,
    NgClass,
    InlineDatePickerComponent,
    InlineUserChipComponent,
    InlineItemStatusChipComponent,
    InlineItemPriorityComponent,
    ItemPriorityComponent,
    DueDateChipComponent,
    StatusCategoryBadgeComponent,
    MatMiniFabButton,
    MatTooltip,
    TranslatePipe,
    ItemsViewControlsComponent,
  ],
  providers: [ItemViewStore, HttpUtilitiesService, RecurringItemService],
  selector: 'app-items-datatable',
  styleUrls: ['items-datatable.component.scss'],
  templateUrl: './items-datatable.component.html',
})
export class ItemsDatatableComponent extends BaseItemView implements OnInit {
  @ViewChild(ItemsModalComponent) public itemsModal!: ComponentType<ItemsModalComponent>;

  private readonly embeddedColumnIds$: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);

  private readonly userSignal: Signal<IUser | null> = this.globalStore.selectSignal(
    (state: TReducerState) => state.user.user,
  );

  public readonly formattedDatatableRows = computed(() => {
    return this.store.data$().map(this.formatData);
  });

  public readonly datatableColumns$: Observable<TDatatableColumn<IDatatableItem>[]> = merge(
    PageConfigurationComponent.onApply.pipe(filter(Boolean)),
    PageConfigurationComponent.configurations$.pipe(
      filter((configurations) => Boolean(configurations?.configurationTabs.length)),
      map(DatatablePageConfigurationComponent.initialDataAsApplyMap),
    ),
  ).pipe(
    map((applyData) => {
      const datatableApplyData = applyData.configurations.find((data) => data.type === 'datatable')!;

      return [
        { id: 'id', isSortable: false },
        ...(datatableApplyData.ids
          .map((columnId): TDatatableColumn<IDatatableItem> | null => {
            const defaultColumn = defaultGeneralInformationDatatableColumns.find((column) => column.id === columnId);
            const customColumn = this.store.customFields().find((customField) => customField.id === columnId);
            const columnName =
              (defaultColumn?.name as keyof IDatatableItem) ??
              (`${customColumn!.name}_${columnId}` as keyof IDatatableItem);

            return !defaultColumn && !customColumn
              ? null
              : {
                  headerClasses: defaultColumn && defaultColumn.headerClasses,
                  id: columnName,
                  isSortable: defaultColumn && defaultColumn.isSortable,
                  ...(customColumn
                    ? {
                        dataViewStrategy: this.getCustomColumnDataViewStrategy(customColumn),
                        name: customColumn.name,
                      }
                    : {}),
                };
          })
          .filter(Boolean) as TDatatableColumn<IDatatableItem>[]),
      ];
    }),
  );

  private readonly filterCardChanged$ = new DynamicObservable(null);

  public datatableConfigurations: IDatatableConfiguration<IDatatableItem> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.none,
    dataRefreshEvent: merge(this.setPageToOneAndRefreshDatatable$, this.filterCardChanged$.obs$),
    deleteScope: ETableCrudScope.bulk,
    duplicateScope: ETableCrudScope.none,
    editScope: ETableCrudScope.single,
    specialRows: [
      {
        deleteCondition(this: DatatableComponent<{ boardId: number; id: number }>): ETableCrudScope {
          return ETableCrudScope.none;
        },
        duplicateCondition(this: DatatableComponent<{ boardId: number; id: number }>): ETableCrudScope {
          return ETableCrudScope.none;
        },
        editCondition(this: DatatableComponent<{ boardId: number; id: number }>): ETableCrudScope {
          return ETableCrudScope.single;
        },
        requirement(this: DatatableComponent<{ boardId: number; id: number }>): boolean {
          return this.checkboxModel.selected.some((rowId) => {
            const selectedRow = this.rowData().find((row) => row.id === rowId);
            const { adminBoardIds } = UserEffects.getUser().authorizations;

            return selectedRow !== undefined && adminBoardIds !== -1 && !adminBoardIds.includes(selectedRow.boardId);
          });
        },
      },
    ],
  };

  constructor(
    private readonly recurringItemService: RecurringItemService,
    private readonly dialog: MatDialog,
  ) {
    super('list');
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.filterCardChanged$.switch(
      this.filterCardData$.pipe(
        filter(Boolean),
        map(() => null),
      ),
    );
    this.subscriptions.push(
      this.getUpdateCustomColumnsSubscription(),
      this.getPageConfigurationUpdateSubscription(),
      this.getSetAsDefaultSubscription(),
      this.getEmbeddedColumnsMessageSubscription(),
    );
    window.parent.postMessage({ payload: ['COLUMNS_CONFIGURATION'], type: 'MESSAGE_LISTENER_ATTACHED' }, '*');
  }

  public override initializeDelete(): void {
    const [selectedRow] = this.selectedRows();

    if (!selectedRow) {
      return;
    }

    const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        cancelButtonText: this.translate.instant('button.cancel'),
        content: this.translate.instant('system.delete.confirmationSingleContent', {
          context: selectedRow[this.context.nameProperty],
        }),
        submitButtonText: this.translate.instant('button.delete'),
        title: this.translate.instant('system.delete.confirmationTitle', {
          context: this.translate.instant(this.context.singleContext),
        }),
      },
    });

    this.subscriptions.push(
      dialogRef
        .afterClosed()
        .pipe(
          switchMap((result) => {
            if (!result) {
              return EMPTY;
            }

            return !selectedRow.itemRecurrenceId
              ? new Observable().pipe(startWith(EItemRecurrenceEditType.onlyThis))
              : this.matDialog
                  .open(ApplyChangesRecursivelyQuestionModalComponent, {
                    data: { mode: ERecursiveAction.delete },
                  })
                  .afterClosed();
          }),
        )
        .subscribe((selectedRecurrenceEditType: EItemRecurrenceEditType | undefined) => {
          if (!selectedRecurrenceEditType) {
            return;
          }

          this.store.deleteOneItem({
            id: selectedRow.id,
            ...this.recurringItemService.getStartDateForAttachmentRecurringUpdate(
              selectedRecurrenceEditType,
              selectedRow.dueDate,
            ),
          });
        }),
    );
  }

  private parseSortByToConfigValue(
    sortBy: EWidgetSortBy | string | null | undefined,
  ): IPageConfigurationValue<string, string>[] {
    if (!sortBy) {
      return [];
    }

    const [columnId, direction] = (sortBy as string).split('_');

    if (!columnId || !direction) {
      return [];
    }

    return [
      {
        name: columnId,
        value: direction.toLowerCase(),
      },
    ];
  }

  private getSetAsDefaultSubscription(): Subscription {
    return PageConfigurationComponent.onApply
      .pipe(
        filter(
          (applyData) =>
            Boolean(applyData?.isSetAsDefault) &&
            applyData!.configurations.some((configuration) => configuration.type === 'datatable'),
        ),
        withLatestFrom(this.getBoardFilterCardSelection()),
      )
      .subscribe(([applyData, boardFilterCardSelection]) => {
        const datatableApplyData = applyData!.configurations.find(
          (configuration) => configuration.type === 'datatable',
        )!;
        const { ids, sortBy } = datatableApplyData;
        const boardConfigName: string = this.getBoardConfigurationName(boardFilterCardSelection);

        const updatedComponentConfigurations = this.buildUpdatedComponentConfigurations(
          boardConfigName,
          ids as number[],
          sortBy,
        );

        this.globalStore.dispatch(
          userActions.updateUserConfigurations({
            pageConfiguration: {
              ...(this.userSignal()?.pageConfiguration ?? {}),
              ItemsComponent:
                updatedComponentConfigurations as unknown as typeof userActions.updateUserConfigurations.prototype.pageConfiguration.ItemsComponent,
            },
          }),
        );
      });
  }

  private getBoardConfigurationName(boardFilterCardSelection: number[] | null): string {
    return boardFilterCardSelection === null ? 'items' : String(boardFilterCardSelection[0]);
  }

  private buildUpdatedComponentConfigurations(
    boardConfigName: string,
    ids: number[],
    sortBy: EWidgetSortBy | string | null | undefined,
  ): { name: string; value: unknown }[] {
    let configurations: { name: string; value: unknown }[] =
      cloneDeep(this.userSignal()?.pageConfiguration?.ItemsComponent) ?? [];

    configurations = this.ensureConfigurationExists(configurations, 'datatable');
    configurations = this.ensureConfigurationExists(configurations, 'datatableSort');

    const formattedSortValue = this.parseSortByToConfigValue(sortBy);

    return configurations.map((config: { name: string; value: unknown }) => {
      const { name, value } = config;

      if (name === 'datatable') {
        return {
          name,
          value: this.getUpdatedBoardValues(
            (value as IPageConfigurationValue<string, number[]>[]) ?? [],
            boardConfigName,
            ids,
          ),
        };
      }

      if (name === 'datatableSort') {
        return {
          name,
          value: this.getUpdatedBoardValues(
            (value as IPageConfigurationValue<string, IPageConfigurationValue<string, string>[]>[]) ?? [],
            boardConfigName,
            formattedSortValue,
          ),
        };
      }

      return config;
    });
  }

  private ensureConfigurationExists(
    configurations: { name: string; value: unknown }[],
    configName: string,
  ): { name: string; value: unknown }[] {
    const exists: boolean = configurations.some(({ name }) => name === configName);

    if (!exists) {
      configurations.push({
        name: configName,
        value: [],
      });
    }

    return configurations;
  }

  private getUpdatedBoardValues<TValue extends number[] | IPageConfigurationValue<string, string>[]>(
    existingValues: IPageConfigurationValue<string, TValue>[],
    boardConfigName: string,
    newValue: TValue,
  ): IPageConfigurationValue<string, TValue>[] {
    const currentValues: IPageConfigurationValue<string, TValue>[] = Array.isArray(existingValues)
      ? existingValues
      : [];

    if (!boardConfigName) {
      return currentValues;
    }

    const filteredValues: IPageConfigurationValue<string, TValue>[] = currentValues.filter(
      ({ name }) => name !== boardConfigName,
    );

    return [...filteredValues, { name: boardConfigName, value: newValue }];
  }

  private getUpdateCustomColumnsSubscription(): Subscription {
    return this.filterCardData$
      .pipe(
        filter(Boolean),
        map(
          (filterCard): ILoadCustomFieldsRequest => ({
            isOnlySelf: filterCard.itemScope === 'self',
            parents: filterCard.board?.length ? filterCard.board.map((board) => board.id) : null,
          }),
        ),
        distinctUntilChanged(isEqual),
      )
      .subscribe((requestParams: ILoadCustomFieldsRequest) => {
        PageConfigurationComponent.setPageConfigurationLoading(true);
        this.store.loadCustomFields(requestParams);
      });
  }

  private getCustomColumnDataViewStrategy(customColumn: IDropdownOption): (row: IDatatableItem) => string {
    return (row: IDatatableItem): string => {
      if (!row.values) {
        return '';
      }

      const selectedValue = row.values.find((value) => value.fieldId === customColumn!.id)?.selectedValue;

      if (!selectedValue) {
        return '';
      }

      if (typeof selectedValue === 'string') {
        return this.removeHtmlTags(selectedValue) ?? '';
      }

      if (Array.isArray(selectedValue)) {
        return selectedValue.map((option) => option.name).join(', ');
      }

      return this.getCustomFieldValueFromDayField(selectedValue);
    };
  }

  private getCustomFieldValueFromDayField(selectedValue: IDateRangePickerDate<string>): string {
    if (selectedValue.startDate === null && selectedValue.endDate === null) {
      return '';
    }

    if (
      selectedValue.startDate === null ||
      selectedValue.endDate === null ||
      selectedValue.startDate === selectedValue.endDate
    ) {
      return selectedValue.startDate
        ? DateUtilitiesService.convertUTCToUserFormatted(selectedValue.startDate, true)
        : DateUtilitiesService.convertUTCToUserFormatted(selectedValue.endDate!, true);
    }

    return `${DateUtilitiesService.convertUTCToUserFormatted(selectedValue.startDate, true)} - ${DateUtilitiesService.convertUTCToUserFormatted(selectedValue.endDate, true)}`;
  }

  private getPageConfigurationUpdateSubscription(): Subscription {
    return combineLatest({
      customFields: this.store.customFields$,
      initiallySelectedHeaders: this.getCompiledInitiallySelectedHeaders(),
    })
      .pipe(
        withLatestFrom(
          this.getBoardFilterCardSelection().pipe(
            map((selectedBoardFilters) => !selectedBoardFilters || selectedBoardFilters.length < 2),
          ),
        ),
      )
      .subscribe(([{ customFields, initiallySelectedHeaders }, isSetAsDefaultAvailable]) => {
        const savedSort = (this.appliedSortBy ?? this.getSavedSortRawValue()) as EWidgetSortBy | undefined;

        PageConfigurationComponent.setPageConfigurationLoading(false);
        PageConfigurationComponent.setPageConfiguration({
          configurationTabs: [
            {
              availableColumnsBySections: [
                {
                  headers: defaultGeneralInformationDatatableColumns.map((row) => ({
                    ...row,
                    name: this.translate.instant(`field.${row.name}`),
                  })),
                  sectionTitle: this.translate.instant('system.dialogSection.generalInformation'),
                },
                {
                  headers: customFields,
                  sectionTitle: this.translate.instant('system.dialogSection.itemFields'),
                },
              ],
              initiallySelectedHeaders,
              lockedHeaders: [-1, -2],
              sortBy: savedSort ?? undefined,
              type: 'datatable',
              unselectedByDefaultHeaders: [],
            },
          ],
          isSetAsDefaultAvailable,
        });
      });
  }

  private getCompiledInitiallySelectedHeaders(): Observable<number[]> {
    return combineLatest({
      apply: merge(
        PageConfigurationComponent.onApply.pipe(
          map((applyData) => applyData?.configurations.find((apply) => apply.type === 'datatable')?.ids ?? null),
        ),
        this.getBoardFilterCardSelection().pipe(map(() => null)),
      ),
      boardFilter: this.getBoardFilterCardSelection(),
      datatableConfiguration: this.globalStore
        .select((state) => state.user.user)
        .pipe(
          map(
            (user): IPageConfigurationValue<string, number[]>[] =>
              (user!.pageConfiguration?.ItemsComponent ?? []).find(
                (configuration: { name: string; value: unknown }) => configuration.name === 'datatable',
              )?.value ?? [],
          ),
        ),
      embeddedColumnIds: this.embeddedColumnIds$,
    }).pipe(
      map(({ boardFilter, datatableConfiguration, apply, embeddedColumnIds }): number[] => {
        if (apply) {
          return apply as number[];
        }

        if (embeddedColumnIds.length) {
          return embeddedColumnIds;
        }

        const relatedSetAsDefaults = datatableConfiguration.filter(
          (boardConfiguration) =>
            (boardFilter === null && boardConfiguration.name === 'items') ||
            (boardFilter !== null && boardConfiguration.name === String(boardFilter[0])),
        );

        return uniq(flatten(relatedSetAsDefaults.map((setAsDefault) => setAsDefault.value)));
      }),
    );
  }

  private getEmbeddedColumnsMessageSubscription(): Subscription {
    return fromEvent<MessageEvent>(window, 'message')
      .pipe(
        map((event): number[] | null => this.parseEmbeddedColumnsMessage(event)),
        filter((columnIds): columnIds is number[] => columnIds !== null),
      )
      .subscribe((columnIds: number[]) => {
        this.embeddedColumnIds$.next(columnIds);
      });
  }

  private parseEmbeddedColumnsMessage(event: MessageEvent): number[] | null {
    const data = event.data as IEmbeddedColumnsMessage | null;

    if (!data || data.type !== 'COLUMNS_CONFIGURATION' || !Array.isArray(data.payload)) {
      return null;
    }

    return Array.from(new Set(data.payload));
  }

  private getBoardFilterCardSelection(): Observable<number[] | null> {
    return this.filterCardData$.pipe(
      filter(Boolean),
      map((filterCard): number[] | null =>
        filterCard.board?.length ? filterCard.board.map((board) => board.id) : null,
      ),
      distinctUntilChanged(isEqual),
    );
  }

  public openWorkInstructions(row: IDatatableItem): void {
    const assignments: IItemWorkInstructionAssignment[] | null = row.assignedWorkInstructions;

    if (!assignments || assignments?.length === 0) {
      return;
    }

    const dialogData = {
      boardId: row.boardId,
      dueDateForRecurrence: null,
      itemId: row.id,
    };

    if (assignments.length === 1) {
      const [assignment] = assignments;

      this.dialog.open(WorkInstructionStepperComponent, {
        ...WorkInstructionEditorComponent.dialogConfig,
        data: {
          ...dialogData,
          itemGuideAssignmentId: assignment.id,
          name: assignment.guide.name,
          workInstructionId: assignment.guideId,
        } as IWorkInstructionStepperParams,
      });
    } else if (assignments.length > 1) {
      this.dialog.open(ItemWorkInstructionListComponent, {
        data: { ...dialogData, onlyOpenMode: true },
        disableClose: true,
        restoreFocus: false,
        width: EDialogWidth.large,
      });
    }
  }
}
